import axios from 'axios';
import { Folder } from '../types';
import api from '../utils/api';
import { FileItem } from '../types/files';

export interface FolderResponse {
  folders: Folder[];
  total: number;
  page: number;
  pageSize: number;
}

interface CreateFolderError extends Error {
  code?: string;
  response?: {
    data?: {
      message?: string;
      code?: string;
    };
    status?: number;
  };
  isAxiosError?: boolean;
}

// Add debounce helper
const debounce = <T extends (...args: any[]) => Promise<any>>(fn: T, ms = 300): T => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    return new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(fn.apply(this, args)), ms);
    });
  } as T;
};

// Request tracking to prevent duplicate requests
const pendingRequests = new Map<string, AbortController>();

const validateFolderName = (name: string): string | null => {
  if (!name || name.trim().length === 0) {
    return 'Folder name is required';
  }
  if (name.length > 255) {
    return 'Folder name must be less than 255 characters';
  }
  if (/[<>:"/\\|?*]/.test(name)) {
    return 'Folder name contains invalid characters';
  }
  return null;
};

const getRequestKey = (name: string, parentId?: string) => {
  return `create_folder:${name}:${parentId || 'root'}`;
};

const NETWORK_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ENETUNREACH'];
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isNetworkError = (error: CreateFolderError): boolean => {
  if (error.isAxiosError) {
    return !error.response || NETWORK_ERROR_CODES.includes(error.code || '');
  }
  return false;
};

const handleCreateFolderError = (error: unknown): never => {
  const apiError = error as CreateFolderError;
  
  if (apiError.isAxiosError) {
    const status = apiError.response?.status;
    const code = apiError.response?.data?.code;
    const message = apiError.response?.data?.message;
    
    // Handle specific error codes from backend
    switch (code) {
      case 'DUPLICATE_REQUEST':
        throw new Error('A request to create this folder is already in progress');
      case 'DUPLICATE_FOLDER':
        throw new Error('A folder with this name already exists in this location');
      case 'PERMISSION_DENIED':
        throw new Error('You do not have permission to create folders in this location');
      case 'PARENT_NOT_FOUND':
        throw new Error('Parent folder not found');
      case 'MAX_DEPTH_EXCEEDED':
        throw new Error('Maximum folder depth limit reached');
      case 'DEADLOCK':
        throw new Error('Operation failed due to concurrent modification. Please try again.');
      default:
        if (status === 400) {
          throw new Error(message || 'Invalid folder name');
        }
        if (status === 403) {
          throw new Error('Permission denied');
        }
        if (status === 409) {
          throw new Error('Folder already exists');
        }
        if (status === 500) {
          throw new Error('Server error. Please try again later.');
        }
    }
  }
  
  // Handle network errors
  if (isNetworkError(apiError)) {
    throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
  }
  
  // Handle unknown errors
  throw new Error('An unexpected error occurred while creating the folder');
};

const folderService = {
  // Debounced version of getFolders
  getFoldersDebounced: debounce(async (parentId?: string | null, includeFiles: boolean = false): Promise<FolderResponse> => {
    const requestKey = `get_folders:${parentId || 'root'}`;
    
    // Cancel any pending request for this folder
    if (pendingRequests.has(requestKey)) {

      pendingRequests.get(requestKey)?.abort();
      pendingRequests.delete(requestKey);
    }
    
    const controller = new AbortController();
    pendingRequests.set(requestKey, controller);
    
    try {
      const params = new URLSearchParams();
      if (parentId) params.append('parentId', parentId);
      if (includeFiles) params.append('includeFiles', 'true');
      
      const endpoint = `/folders?${params.toString()}`;

      const response = await api.get(endpoint, {
        signal: controller.signal
      });

      return response.data;
    } finally {
      pendingRequests.delete(requestKey);
    }
  }),

  // Original getFolders method now uses the debounced version
  async getFolders(parentId?: string | null, includeFiles: boolean = false): Promise<FolderResponse> {

    return this.getFoldersDebounced(parentId, includeFiles);
  },

  async getFolder(folderId: string, options?: { signal?: AbortSignal }): Promise<Folder> {
    const response=await api.get(`/folders/${folderId}`,{signal:options?.signal,params:{metadataOnly:true}});
    return response.data.folder;
  },

  async getFolderDetails(
    folderId: string,
    options?: { signal?: AbortSignal }
  ): Promise<{
    folder: Folder;
    subfolders: Folder[];
    files: FileItem[];
  }> {
    const response = await api.get(`/folders/${folderId}`, { signal: options?.signal });
    return {
      folder: response.data.folder,
      subfolders: response.data.subfolders ?? [],
      files: response.data.files ?? [],
    };
  },

  async createFolder(name: string, parentId?: string): Promise<Folder> {
    // Validate folder name first
    const validationError = validateFolderName(name);
    if (validationError) {
      throw new Error(validationError);
    }

    const requestKey = getRequestKey(name, parentId);
    
    // Check for existing request
    if (pendingRequests.has(requestKey)) {
      throw new Error('A request to create this folder is already in progress');
    }
    
    // Create abort controller for this request
    const controller = new AbortController();
    pendingRequests.set(requestKey, controller);
    
    try {
      let lastError: CreateFolderError | null = null;
      let isNetworkFailure = false;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Only log on retry attempts
          if (attempt > 1) {

          } else {

          }
          
          const response = await api.post(
            '/folders/create',
            { name, parentId },
            { 
              timeout: 5000, // 5 second timeout
              headers: {
                'X-Retry-Attempt': attempt.toString()
              },
              signal: controller.signal
            }
          );

          if (response.data?.folder) {

            return response.data.folder;
          } else {

            throw new Error('Invalid response format from server');
          }
        } catch (error: unknown) {
          // Check if request was aborted
          if (axios.isCancel(error)) {

            throw new Error('Folder creation was cancelled');
          }
          
          const apiError = error as CreateFolderError;
          lastError = apiError;

          // Check if it's a network error
          isNetworkFailure = isNetworkError(apiError);
          
          // Don't retry on validation, permission, or duplicate errors
          if (apiError.response?.status === 400 || 
              apiError.response?.status === 403 || 
              apiError.response?.status === 409) {
            handleCreateFolderError(apiError);
          }
          
          // Only retry on network errors or 5xx server errors
          if (!isNetworkFailure && apiError.response?.status && apiError.response.status < 500) {
            handleCreateFolderError(apiError);
          }
          
          // If this was the last attempt, throw a more specific error
          if (attempt === MAX_RETRIES) {
            handleCreateFolderError(lastError);
          }
          
          // Wait before retrying with exponential backoff
          await sleep(RETRY_DELAY * Math.pow(2, attempt - 1));
        }
      }
      
      // This should never be reached due to the throw in the loop
      throw lastError || new Error('Failed to create folder');
    } finally {
      // Clean up the request
      pendingRequests.delete(requestKey);
    }
  },

  // Add method to cancel pending folder creation
  cancelCreateFolder(name: string, parentId?: string): void {
    const requestKey = getRequestKey(name, parentId);
    const controller = pendingRequests.get(requestKey);
    if (controller) {
      controller.abort();
      pendingRequests.delete(requestKey);
    }
  },

  async updateFolder(folderId: string, data: Partial<Folder>): Promise<Folder> {
    const response = await api.patch(
      `/folders/${folderId}`,
      data
    );
    return response.data;
  },

  async deleteFolder(folderId: string, recursive: boolean = false, permanent: boolean = false): Promise<void> {
    await api.delete(
      `/folders/${folderId}`,
      { params: { recursive, permanent } }
    );
  },

  async restoreFolder(folderId: string): Promise<void> {
    await api.post(`/folders/${folderId}/restore`);
  },

  async copyFolder(folderId: string, targetParentId: string | undefined): Promise<Folder> {
    const response = await api.post(
      '/folders/copy',
      {items:[{id:folderId,type:'folder'}],destinationId:targetParentId||'root'}
    );
    return response.data;
  },

  async moveFolder(folderId: string, targetParentId: string | undefined): Promise<Folder> {
    const response = await api.post(
      '/folders/move',
      {items:[{id:folderId,type:'folder'}],destinationId:targetParentId||'root'}
    );
    return response.data;
  },

  async renameFolder(folderId: string, newName: string): Promise<Folder> {
    const response = await api.patch(
      `/folders/${folderId}/rename`,
      { name: newName }
    );
    return response.data;
  },
  
  async search(query: string, filters: any = {}): Promise<FolderResponse> {
    const response = await api.get(
      `/folders/search?query=${encodeURIComponent(query)}`,
      {
        params: filters
      }
    );
    return response.data;
  },

  /**
   * Get contents of a folder (both folders and files)
   * @param folderId The ID of the folder to get contents from, or 'root' for root folder
   * @param params Optional parameters for sorting, filtering, etc.
   * @returns Promise with the folder contents
   */
  async getFolderContents(
    folderId: string,
    params?: {
      search?: string;
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
      tags?: string[];
      page?: number;
      cursor?: string;
      fileType?: string;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<{ items: Array<FileItem | Folder>; total: number; pagination?: {hasMore:boolean;nextCursor:string|null;limit:number} }> {
    try {
      // Build query parameters
      const queryParams: any = {};
      if (params?.search) queryParams.search = params.search;
      if (params?.sortBy) queryParams.sortBy = params.sortBy;
      if (params?.sortDirection) queryParams.sortDirection = params.sortDirection;
      if (params?.cursor) queryParams.cursor=params.cursor;
      if (params?.fileType) queryParams.fileType=params.fileType;
      if (params?.page) queryParams.page = params.page;
      if (params?.limit) queryParams.limit = params.limit;
      if (params?.tags) queryParams.tags = params.tags;

      // Fetch both folders and files from folder contents endpoint
      const response = await api.get(`/folders/${folderId}/contents`, {
        params: queryParams,
        signal: params?.signal
      });
      // Support both new and old API response structures
      const data = response.data;
      const folders = data.subfolders || data.contents?.folders || [];
      const files = data.files || data.contents?.files || [];

      // Mark items with their type
      const folderItems = folders.map((folder: Folder) => ({ ...folder, type: 'folder' }));
      const fileItems = files.map((file: FileItem) => ({ ...file, mimeType:file.mimeType||file.mimetype, type: 'file' }));

      const items = [...folderItems, ...fileItems];

      return { items, total: data.total ?? items.length, pagination:data.pagination };
    } catch (error) {

      throw error;
    }
  }
};

// For backward compatibility with existing imports
export { folderService };

export default folderService;